import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Target, PencilSimple, Check, X } from "@phosphor-icons/react";

export default function Targets() {
  const [defaultVal, setDefaultVal] = useState(30);
  const [editingDefault, setEditingDefault] = useState(false);
  const [pendingDefault, setPendingDefault] = useState(30);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editingCand, setEditingCand] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/candidate-targets");
      setDefaultVal(data.default);
      setRows(data.rows);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveDefault = async () => {
    try {
      const { data } = await api.put("/targets/global-default", { value: Number(pendingDefault) });
      setDefaultVal(data.value);
      setEditingDefault(false);
      toast.success("Default updated");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const saveCandidate = async (cid, val) => {
    try {
      await api.patch(`/candidates/${cid}/target`, { daily_target: Number(val) });
      setEditingCand(null);
      toast.success("Target saved");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const resetToDefault = async (cid) => {
    if (!window.confirm("Reset to global default?")) return;
    try {
      await api.patch(`/candidates/${cid}/target`, { daily_target: defaultVal });
      toast.success("Reset");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const filtered = rows.filter((r) => !q || (r.full_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.assigned_employee_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.placement_field || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="targets-page">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2"><Target size={22} /> Candidate Daily Targets</h2>
        <p className="text-sm text-slate-500">Targets are configured per candidate — different roles justify different daily submission counts.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="global-default-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Global Default</div>
            <div className="text-sm text-slate-500">Applied automatically when a candidate is first assigned.</div>
          </div>
          {editingDefault ? (
            <div className="flex items-center gap-2">
              <input type="number" min="1" max="500" value={pendingDefault}
                     onChange={(e) => setPendingDefault(e.target.value)}
                     data-testid="default-target-input"
                     className="w-24 border border-slate-200 rounded-md px-3 py-2 text-sm" />
              <button onClick={saveDefault} data-testid="save-default"
                      className="text-emerald-700 hover:text-emerald-900 p-2"><Check size={16} /></button>
              <button onClick={() => setEditingDefault(false)} className="text-slate-400 hover:text-slate-700 p-2"><X size={16} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="text-3xl font-semibold" data-testid="default-target-value">{defaultVal}</div>
              <span className="text-xs text-slate-500">applications / day</span>
              <button onClick={() => { setPendingDefault(defaultVal); setEditingDefault(true); }}
                      data-testid="edit-default" className="text-slate-500 hover:text-sky-700 p-2">
                <PencilSimple size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input placeholder="Search by candidate, employee or placement field…" value={q}
               onChange={(e) => setQ(e.target.value)} data-testid="target-search"
               className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm" />
        <div className="text-xs text-slate-500">{filtered.length} candidates</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Candidate</th>
              <th className="text-left px-3 py-2">Placement / Role</th>
              <th className="text-left px-3 py-2">Assigned to</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Target</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-8">No matching candidates.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.candidate_id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{r.full_name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {r.placement_field || "—"} <span className="text-slate-300">/</span> {r.preferred_role || "—"}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.assigned_employee_name || <span className="text-slate-400">unassigned</span>}
                  {r.assigned_team && <div className="text-slate-400">{r.assigned_team}</div>}
                </td>
                <td className="px-3 py-2 text-xs">{r.service_status}</td>
                <td className="px-3 py-2 text-right">
                  {editingCand === r.candidate_id ? (
                    <div className="inline-flex items-center gap-1">
                      <input type="number" defaultValue={r.effective_target} min="0" max="500" autoFocus
                             data-testid={`target-input-${r.candidate_id}`}
                             onKeyDown={(e) => { if (e.key === "Enter") saveCandidate(r.candidate_id, e.target.value); if (e.key === "Escape") setEditingCand(null); }}
                             onBlur={(e) => saveCandidate(r.candidate_id, e.target.value)}
                             className="w-20 border border-slate-200 rounded-md px-2 py-1 text-sm text-right" />
                    </div>
                  ) : (
                    <button onClick={() => setEditingCand(r.candidate_id)} data-testid={`edit-target-${r.candidate_id}`}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded ${r.using_default ? "text-slate-500 hover:bg-slate-100" : "text-sky-700 hover:bg-sky-50 font-medium"}`}>
                      {r.effective_target}
                      {r.using_default && <span className="text-[10px] text-slate-400 ml-1">(default)</span>}
                      <PencilSimple size={12} className="opacity-40 group-hover:opacity-100" />
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {!r.using_default && (
                    <button onClick={() => resetToDefault(r.candidate_id)} className="text-xs text-slate-500 hover:text-slate-800">
                      Reset
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
